// components/ServicesSection/ServicesSection.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { motion } from 'framer-motion';
import axios from 'axios';

// Icons
import { FaArrowRight, FaArrowLeft } from 'react-icons/fa';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './css/ServicesSection.css';

import SecondImg from './images/4.jpg';
import { API_BASE } from '../config/api';

const DEFAULT_SECONDARY_IMAGE =
  'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1000&auto=format&fit=crop';

function toAbsoluteImageUrl(maybeUrl, apiBase) {
  if (!maybeUrl) return '';
  const u = String(maybeUrl).trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${apiBase}${u}`;
}

function formatServicePrice(service) {
  if (service.price !== null && service.price !== undefined) {
    return `${service.price} ريال`;
  }

  const m = service.morningPrice;
  const e = service.eveningPrice;
  if (m !== null && m !== undefined && e !== null && e !== undefined) {
    if (Number(m) === Number(e)) return `${m} ريال`;
    return `${m} - ${e} ريال`;
  }

  if (m !== null && m !== undefined) return `${m} ريال`;
  if (e !== null && e !== undefined) return `${e} ريال`;
  return '-';
}

function getSliderPriceText(service) {
  // Match the old hardcoded ServicesSection display:
  // - phone & defense: show base price only
  // - zoom: show 150 ريال (morning) even if evening differs
  // - others (e.g. review): use range logic
  if (service.id === 'zoom') {
    if (service.morningPrice !== null && service.morningPrice !== undefined) {
      return `${service.morningPrice} ريال`;
    }
  }

  if (service.id === 'phone' || service.id === 'defense') {
    if (service.price !== null && service.price !== undefined) {
      return `${service.price} ريال`;
    }
  }

  return formatServicePrice(service);
}

function getServiceNote(service) {
  const firstFeature =
    Array.isArray(service.features) && service.features.length
      ? service.features[0]
      : null;
  return firstFeature || 'الخدمة مخصصة للطالبات فقط';
}

const ServicesSection = () => {
  const defaultServicesSection = useMemo(
    () => ({
      section_label: 'الخدمات والأسعار',
      section_heading: 'استثمارك في مستقبلك الأكاديمي',
      collage_main_image_url: null, // keep local import fallback
      collage_secondary_image_url: DEFAULT_SECONDARY_IMAGE,
    }),
    []
  );

  const [servicesSection, setServicesSection] = useState(
    defaultServicesSection
  );
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [secRes, srvRes] = await Promise.all([
          axios.get(`${API_BASE}/api/services-section`),
          axios.get(`${API_BASE}/api/services`),
        ]);

        if (!mounted) return;

        const sec = secRes.data?.servicesSection;
        const srv = srvRes.data?.services;

        setServicesSection(
          sec ? { ...defaultServicesSection, ...sec } : defaultServicesSection
        );
        setServices(Array.isArray(srv) ? srv : []);
      } catch (err) {
        console.error('Failed to load services section:', err);
        if (!mounted) return;
        setServicesSection(defaultServicesSection);
        setServices([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    fetchAll();

    return () => {
      mounted = false;
    };
  }, [defaultServicesSection]);

  const imgMain = servicesSection.collage_main_image_url
    ? toAbsoluteImageUrl(servicesSection.collage_main_image_url, API_BASE) ||
      SecondImg
    : SecondImg;

  const imgSecondary = servicesSection.collage_secondary_image_url
    ? toAbsoluteImageUrl(
        servicesSection.collage_secondary_image_url,
        API_BASE
      ) || DEFAULT_SECONDARY_IMAGE
    : DEFAULT_SECONDARY_IMAGE;

  const sectionLabel =
    servicesSection.section_label || defaultServicesSection.section_label;
  const sectionHeading =
    servicesSection.section_heading ||
    defaultServicesSection.section_heading;

  const sliderCards = useMemo(() => {
    return (services || []).map((s) => ({
      id: s.id,
      title: s.name,
      priceText: getSliderPriceText(s),
      duration: s.duration || '',
      desc: s.description || '',
      note: getServiceNote(s),
    }));
  }, [services]);

  return (
    <section className="services-section" dir="rtl">
      <div className="container">
        <div className="row align-items-center">
          {/* 1. Image Collage Column */}
          <div className="col-lg-6 mb-5 mb-lg-0 relative-wrapper">
            <motion.div
              className="collage-container"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Background Decoration Blob */}
              <div className="blob-decoration"></div>

              {/* Main Portrait */}
              <div className="img-wrapper img-main">
                <img src={imgMain} alt="Consultant" />
              </div>

              {/* Secondary Image */}
              <div className="img-wrapper img-secondary">
                <img src={imgSecondary} alt="Working" />
              </div>

              {/* Dots Pattern */}
              <div className="dots-decoration"></div>
            </motion.div>
          </div>

          {/* 2. Content & Slider Column */}
          <div className="col-lg-6">
            <motion.div
              className="content-wrapper"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h6 className="section-label">{sectionLabel}</h6>
              <h2 className="section-heading">{sectionHeading}</h2>

              <div className="slider-wrapper mt-5">
                <Swiper
                  modules={[Navigation, Pagination]}
                  spaceBetween={30}
                  slidesPerView={1}
                  navigation={{
                    nextEl: '.custom-next',
                    prevEl: '.custom-prev',
                  }}
                  loop={true}
                  className="services-swiper"
                >
                  {(loading ? sliderCards : sliderCards).map((service) => (
                    <SwiperSlide key={service.id}>
                      <div className="service-card">
                        <div className="card-header">
                          <span className="service-price">
                            {service.priceText}
                          </span>
                          <span className="service-duration">
                            {service.duration ? `/ ${service.duration}` : ''}
                          </span>
                        </div>
                        <h3 className="service-title">{service.title}</h3>
                        <p className="service-desc">{service.desc}</p>
                        <div className="service-footer">
                          <span className="highlight-note">
                            💡 {service.note}
                          </span>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Custom Navigation Arrows */}
                <div className="slider-controls">
                  <button className="custom-prev" type="button">
                    <FaArrowRight />
                  </button>
                  <button className="custom-next" type="button">
                    <FaArrowLeft />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;